<template>
  <div class="form3-container">
    <div class="section-header">
      <div class="header-left">
        <h2 class="section-title">关卡谜题提示配置</h2>
        <span class="section-tip"
          >最多支持 4 级逐级引导提示，右侧可点击体验 App 端遮罩解密效果</span
        >
      </div>
      <el-button
        type="primary"
        :icon="Plus"
        class="add-tip-btn"
        :disabled="fileList2.length >= 4"
        @click="addTip(detailId)"
      >
        新增提示 ({{ fileList2.length }}/4)
      </el-button>
    </div>

    <!-- 提示卡片拖拽选择栏 -->
    <div class="tip-tabs-bar">
      <VueDraggable
        ref="el"
        v-model="fileList2"
        class="draggable-tip-tabs"
        :animation="150"
      >
        <div
          v-for="(item, index) in fileList2"
          :key="item.id || index"
          :class="['tip-tab-chip', { active: activeIndex === index }]"
          @click="changeTip(index)"
        >
          <div class="drag-handle">
            <span class="dot"></span>
            <span class="dot"></span>
          </div>
          <span class="tab-name">{{ `提示 ${index + 1}` }}</span>
          <el-icon
            class="tab-close"
            title="删除此提示"
            @click.stop="deleteTip(index, $event)"
          >
            <Close />
          </el-icon>
        </div>
      </VueDraggable>
    </div>

    <!-- 主体双栏布局 -->
    <div class="editor-layout" v-if="fileList2.length > 0">
      <!-- 左侧：表单配置区 -->
      <div class="form-area">
        <el-form
          :model="form"
          label-width="auto"
          label-position="top"
          :rules="rules"
          ref="ruleForm"
          status-icon
          class="custom-form"
        >
          <el-form-item label="提示标题" prop="puzzleTitle">
            <el-input
              v-model="form.puzzleTitle"
              placeholder="例如：关于书架暗格的线索"
              clearable
            />
          </el-form-item>

          <el-form-item label="提示正文内容" prop="puzzleContent">
            <el-input
              v-model="form.puzzleContent"
              type="textarea"
              :rows="7"
              maxlength="1000"
              show-word-limit
              placeholder="请输入循序渐进的引导文字，帮助玩家解开谜题..."
            />
          </el-form-item>

          <el-form-item label="提示参考附图（选填）" prop="puzzleCover">
            <div class="single-uploader-card">
              <el-upload
                class="uploader-box"
                :action="UpdateApi"
                :show-file-list="false"
                :on-success="handleAvatarSuccess"
                :before-upload="beforeAvatarUpload"
                :disabled="allowUpload"
              >
                <div v-if="form.puzzleCover" class="img-preview-box">
                  <el-image
                    :src="form.puzzleCover"
                    class="img-content"
                    fit="cover"
                  />
                  <div class="hover-mask">
                    <span
                      class="del-btn"
                      @click.stop="clearImage"
                      title="删除图片"
                    >
                      <el-icon><Delete /></el-icon>
                    </span>
                  </div>
                </div>
                <div v-else class="upload-trigger">
                  <el-icon class="icon"><Plus /></el-icon>
                  <span class="text">上传提示配图</span>
                  <span class="text">支持 4:3 | 1:1 | 3:4 / 小于 5MB</span>
                </div>
              </el-upload>
            </div>
          </el-form-item>
        </el-form>
      </div>

      <!-- 右侧：现代手机拟物化真机视口 -->
      <div class="preview-area">
        <div class="preview-header">
          <span class="badge-dot"></span>
          <span>移动端解密效果预览</span>
        </div>

        <div class="mockup-phone">
          <div class="phone-top-bar">
            <div class="notch"></div>
          </div>

          <div class="phone-screen">
            <!-- 手机导航条 -->
            <div class="mock-nav">
              <el-icon><ArrowLeft /></el-icon>
              <span class="nav-title">{{
                `关卡提示 (${activeIndex + 1}/${fileList2.length})`
              }}</span>
              <el-icon><ArrowRight /></el-icon>
            </div>

            <!-- 提示内容展示区 -->
            <div class="screen-scrollable">
              <h4 class="tip-preview-title">
                {{ form.puzzleTitle || "提示标题占位" }}
              </h4>

              <!-- 状态一：已开启解密 -->
              <transition name="fade">
                <div v-if="showTip" class="unlocked-tip-content">
                  <div class="tip-img-box" v-if="form.puzzleCover">
                    <el-image
                      :src="form.puzzleCover"
                      fit="cover"
                      class="tip-img"
                    />
                  </div>
                  <div class="tip-text-body">
                    {{ form.puzzleContent || "这里展示详细的文字线索指引..." }}
                  </div>
                </div>
              </transition>

              <!-- 状态二：未开启遮罩锁定 -->
              <div v-if="!showTip" class="locked-tip-placeholder">
                <div class="lock-icon-circle">
                  <el-icon><QuestionFilled /></el-icon>
                </div>
                <span class="lock-title">提示内容已锁定</span>
                <span class="lock-sub">点击下方按钮可消耗积分查看本条线索</span>
              </div>
            </div>

            <!-- 底部交互操作栏 -->
            <div class="screen-footer">
              <button
                :class="['mock-action-btn', { active: showTip }]"
                @click="showTip = !showTip"
              >
                {{ showTip ? "收起提示内容" : "开启并查看提示" }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 无提示时的缺省引导 -->
    <div v-else class="empty-placeholder">
      <el-icon class="empty-icon"><InfoFilled /></el-icon>
      <p class="empty-title">当前关卡尚未创建任何提示</p>
      <p class="empty-desc">建议添加 1~2 条梯度提示，避免玩家因卡关而流失</p>
      <el-button type="primary" plain :icon="Plus" @click="addTip(detailId)">
        立即创建第 1 条提示
      </el-button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, reactive } from "vue";
import {
  ArrowLeft,
  ArrowRight,
  Plus,
  QuestionFilled,
  Delete,
  Close,
  InfoFilled,
} from "@element-plus/icons-vue";
import { ElMessage, ElNotification, FormRules } from "element-plus";
import { VueDraggable } from "vue-draggable-plus";

const validateNotEmpty = (rule: any, value: string, callback: any) => {
  if (!value || value.trim() === "") {
    callback(new Error(rule.message));
  } else {
    callback();
  }
};

export default defineComponent({
  name: "PuzzleTipForm",
  components: {
    ArrowLeft,
    ArrowRight,
    QuestionFilled,
    Plus,
    Delete,
    Close,
    InfoFilled,
    VueDraggable,
  },
  props: ["dataForm"],
  data(): any {
    return {
      form: {
        puzzleTitle: "",
        puzzleContent: "",
        puzzleCover: "",
      },
      fileList2: [],
      showTip: true,
      detailId: "",
      activeIndex: 0,
      rules: reactive<FormRules>({
        puzzleTitle: [
          {
            required: true,
            message: "提示标题不能为空",
            trigger: "blur",
            validator: validateNotEmpty,
          },
        ],
        puzzleContent: [
          {
            required: true,
            message: "提示正文内容不能为空",
            trigger: "blur",
            validator: validateNotEmpty,
          },
        ],
      }),
    };
  },
  computed: {
    allowUpload(): boolean {
      return Boolean(this.form.puzzleCover);
    },
    UpdateApi(): string {
      return process.env.VUE_APP_Update_API || "";
    },
  },
  watch: {
    form: {
      handler() {
        if (this.fileList2[this.activeIndex]) {
          this.fileList2[this.activeIndex].tipTitle = this.form.puzzleTitle;
          this.fileList2[this.activeIndex].tipContent = this.form.puzzleContent;
          this.fileList2[this.activeIndex].tipPicture = this.form.puzzleCover;
        }
      },
      deep: true,
    },
    dataForm: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (newVal && newVal.tips) {
          this.getTipList(newVal.tips);
        }
      },
    },
  },
  mounted() {
    const { detailId } = this.$route.params;
    this.detailId = detailId as string;
  },
  methods: {
    beforeAvatarUpload(file: File): boolean {
      const isAllowed =
        file.type === "image/jpeg" ||
        file.type === "image/png" ||
        file.type === "image/webp";
      const isLt5M = file.size / 1024 / 1024 < 5;

      if (!isAllowed) {
        ElNotification.warning({
          message: "仅支持 JPG/PNG/WEBP 格式图片",
          offset: 100,
        });
        return false;
      }
      if (!isLt5M) {
        ElNotification.warning({
          message: "图片大小不可超过 5MB",
          offset: 100,
        });
        return false;
      }
      return true;
    },
    handleAvatarSuccess(response: any) {
      if (response && response.data && response.data.returnFileUrl) {
        this.form.puzzleCover = response.data.returnFileUrl;
      }
    },

    async clearImage() {
      const fileName = this.form.puzzleCover;

      if (!fileName) return;

      try {
        // 1. 匹配 action 参数签名（传对象）
        const delFinished = await this.$store.dispatch("user/delFile", {
          fileName,
        });

        // 2. 只有删除成功后才清空前端字段
        if (delFinished === 1) {
          this.form.puzzleCover = "";
        }
      } catch (error) {
        console.error("清除图片失败:", error);
      }
    },
    addTip(belongId: string) {
      if (this.fileList2.length >= 4) {
        ElMessage.warning("单关卡最多支持创建 4 个提示");
        return;
      }

      this.$store
        .dispatch("game/addTip", { belongId: belongId })
        .then((res: any) => {
          res.key = this.fileList2.length;
          res.active = true;
          this.fileList2.push(res);
          this.changeTip(this.fileList2.length - 1);
        })
        .catch((err: any) => {
          console.error(err);
        });
    },
    getTipList(ids: string) {
      this.$store
        .dispatch("game/getTipList", { tips: ids })
        .then((res: any) => {
          if (Array.isArray(res)) {
            this.fileList2 = res.map((item: any, index: number) => ({
              ...item,
              key: index,
              active: index === 0,
            }));
            if (this.fileList2.length > 0) {
              this.changeTip(0);
            }
          }
        })
        .catch((err: any) => {
          console.error(err);
        });
    },
    changeTip(index: number) {
      if (index < 0 || index >= this.fileList2.length) return;
      this.fileList2.forEach((item: any) => {
        item.active = false;
      });
      this.fileList2[index].active = true;
      this.activeIndex = index;

      this.form.puzzleTitle = this.fileList2[index].tipTitle || "";
      this.form.puzzleContent = this.fileList2[index].tipContent || "";
      this.form.puzzleCover = this.fileList2[index].tipPicture || "";
      this.showTip = true;
    },
    deleteTip(index: number, event: Event) {
      event.stopPropagation();
      const target = this.fileList2[index];
      if (!target) return;

      this.$store
        .dispatch("game/delTip", {
          levelId: this.detailId,
          tipId: target.id,
        })
        .then(() => {
          this.fileList2.splice(index, 1);
          if (this.fileList2.length === 0) {
            this.form.puzzleTitle = "";
            this.form.puzzleContent = "";
            this.form.puzzleCover = "";
            this.activeIndex = 0;
          } else {
            const nextIdx =
              index >= this.fileList2.length
                ? this.fileList2.length - 1
                : index;
            this.changeTip(nextIdx);
          }
        });
    },
    saveTip() {
      const tipIds = this.fileList2.map((item: any) => item.id).join(",");
      this.$store
        .dispatch("game/updateTip", {
          levelId: this.detailId,
          ids: tipIds,
          tips: this.fileList2,
        })
        .catch((err: any) => {
          console.error(err);
        });
    },
  },
});
</script>

<style lang="less" scoped>
@primary-color: #5d5fef;
@primary-light: #f0f2ff;
@text-main: #1e293b;
@text-sub: #64748b;
@text-muted: #94a3b8;
@border-color: #e2e8f0;
@card-border: #f1f5f9;
@danger-color: #ef4444;

.form3-container {
  width: 100%;
}

// 头部说明与操作
.section-header {
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid @border-color;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .header-left {
    display: flex;
    align-items: baseline;
    gap: 12px;

    .section-title {
      font-size: 17px;
      font-weight: 700;
      color: @text-main;
      margin: 0;
    }

    .section-tip {
      font-size: 13px;
      color: @text-muted;
    }
  }

  .add-tip-btn {
    border-radius: 8px;
    font-weight: 600;
    background-color: @primary-color;
    border-color: @primary-color;
    box-shadow: 0 4px 10px rgba(93, 95, 239, 0.25);
    transition: all 0.2s ease;

    &:hover {
      background-color: #4b4de0;
      border-color: #4b4de0;
      transform: translateY(-1px);
    }
  }
}

// 提示标签选择栏（拖拽芯片式）
.tip-tabs-bar {
  margin-bottom: 24px;

  .draggable-tip-tabs {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .tip-tab-chip {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    border-radius: 8px;
    background: #f8fafc;
    border: 1px solid @border-color;
    color: @text-sub;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    user-select: none;
    transition: all 0.2s ease;

    .drag-handle {
      display: flex;
      flex-direction: column;
      gap: 2px;
      cursor: grab;

      .dot {
        width: 3px;
        height: 3px;
        background: #cbd5e1;
        border-radius: 50%;
      }
    }

    .tab-name {
      letter-spacing: 0.3px;
    }

    .tab-close {
      font-size: 13px;
      color: @text-muted;
      border-radius: 50%;
      padding: 2px;
      transition: all 0.2s;

      &:hover {
        background: #fee2e2;
        color: @danger-color;
      }
    }

    &:hover {
      border-color: #cbd5e1;
      background: #f1f5f9;
      color: @text-main;
    }

    &.active {
      background: @primary-light;
      border-color: #dce2ff;
      color: @primary-color;
      box-shadow: 0 2px 8px rgba(93, 95, 239, 0.12);

      .drag-handle .dot {
        background: @primary-color;
      }
    }
  }
}

// 主体双栏布局
.editor-layout {
  display: flex;
  gap: 40px;
  align-items: flex-start;

  @media (max-width: 992px) {
    flex-direction: column;
    gap: 32px;
  }
}

// 左侧表单排版
.form-area {
  flex: 1;
  max-width: 600px;
  width: 100%;

  .custom-form {
    :deep(.el-form-item) {
      margin-bottom: 22px;

      .el-form-item__label {
        font-size: 13.5px;
        font-weight: 600;
        color: @text-main;
        padding-bottom: 6px;
      }
    }

    :deep(.el-input__wrapper),
    :deep(.el-textarea__inner) {
      border-radius: 8px;
      box-shadow: 0 0 0 1px @border-color inset;
      transition: all 0.2s ease;

      &:hover {
        box-shadow: 0 0 0 1px #cbd5e1 inset;
      }

      &.is-focus,
      &:focus {
        box-shadow: 0 0 0 1px @primary-color inset !important;
      }
    }
  }
}

// 单图上传卡片
.single-uploader-card {
  .uploader-box {
    :deep(.el-upload) {
      width: 100%;
      display: block;
      cursor: pointer;
    }
  }

  .img-preview-box {
    position: relative;
    width: 100%;
    height: 160px;
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid @border-color;
    background: #f8fafc;

    .img-content {
      width: 100%;
      height: 100%;
      display: block;
    }

    .hover-mask {
      position: absolute;
      inset: 0;
      background: rgba(15, 23, 42, 0.55);
      backdrop-filter: blur(2px);
      display: flex;
      justify-content: center;
      align-items: center;
      opacity: 0;
      transition: opacity 0.25s ease;

      .del-btn {
        width: 38px;
        height: 38px;
        border-radius: 50%;
        background: #ffffff;
        color: @danger-color;
        display: flex;
        justify-content: center;
        align-items: center;
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
        transition: transform 0.2s;

        &:hover {
          transform: scale(1.12);
        }
      }
    }

    &:hover .hover-mask {
      opacity: 1;
    }
  }

  .upload-trigger {
    width: 100%;
    height: 120px;
    border: 1.5px dashed #cbd5e1;
    border-radius: 10px;
    background: #f8fafc;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 6px;
    transition: all 0.2s ease;

    .icon {
      font-size: 20px;
      color: @text-sub;
    }

    .text {
      font-size: 13px;
      font-weight: 500;
      color: @text-main;
    }

    &:hover {
      border-color: @primary-color;
      background: @primary-light;

      .icon,
      .text {
        color: @primary-color;
      }
    }
  }
}

// 右侧拟物化真机视口
.preview-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;

  .preview-header {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    color: @text-sub;
    margin-bottom: 14px;

    .badge-dot {
      width: 8px;
      height: 8px;
      background: #10b981;
      border-radius: 50%;
      box-shadow: 0 0 6px rgba(16, 185, 129, 0.6);
    }
  }
}

.mockup-phone {
  width: 290px;
  height: 520px;
  background: #ffffff;
  border-radius: 36px;
  border: 10px solid #0f172a;
  box-shadow: 0 16px 36px -6px rgba(15, 23, 42, 0.16);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .phone-top-bar {
    width: 100%;
    height: 20px;
    display: flex;
    justify-content: center;
    background: #ffffff;
    z-index: 10;

    .notch {
      width: 76px;
      height: 12px;
      background: #0f172a;
      border-bottom-left-radius: 6px;
      border-bottom-right-radius: 6px;
    }
  }

  .phone-screen {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: #f8fafc;
  }

  .mock-nav {
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 14px;
    border-bottom: 1px solid @border-color;
    background: #ffffff;
    font-size: 13px;
    color: @text-main;

    .nav-title {
      font-weight: 700;
    }
  }

  .screen-scrollable {
    flex: 1;
    padding: 16px 14px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;

    .tip-preview-title {
      font-size: 14px;
      font-weight: 700;
      color: @text-main;
      margin: 0 0 12px 0;
      line-height: 1.4;
      text-align: center;
    }
  }

  .unlocked-tip-content {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .tip-img-box {
      width: 100%;
      height: 140px;
      border-radius: 8px;
      overflow: hidden;
      border: 1px solid @border-color;
      background: #ffffff;

      .tip-img {
        width: 100%;
        height: 100%;
        display: block;
      }
    }

    .tip-text-body {
      font-size: 12.5px;
      color: @text-main;
      line-height: 1.65;
      white-space: pre-wrap;
      word-break: break-all;
    }
  }

  .locked-tip-placeholder {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px 10px;
    text-align: center;

    .lock-icon-circle {
      width: 56px;
      height: 56px;
      border-radius: 50%;
      background: #eef2ff;
      color: @primary-color;
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 26px;
      margin-bottom: 12px;
    }

    .lock-title {
      font-size: 13px;
      font-weight: 700;
      color: @text-main;
      margin-bottom: 4px;
    }

    .lock-sub {
      font-size: 11.5px;
      color: @text-muted;
      line-height: 1.4;
    }
  }

  .screen-footer {
    padding: 10px 14px 14px;
    background: #ffffff;
    border-top: 1px solid @border-color;

    .mock-action-btn {
      width: 100%;
      height: 34px;
      border-radius: 17px;
      border: none;
      background: @primary-color;
      color: #ffffff;
      font-size: 12.5px;
      font-weight: 600;
      cursor: pointer;
      box-shadow: 0 3px 8px rgba(93, 95, 239, 0.25);
      transition: all 0.2s ease;

      &.active {
        background: #64748b;
        box-shadow: none;
      }
    }
  }
}

// 缺省占位区
.empty-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  background: #f8fafc;
  border: 1.5px dashed @border-color;
  border-radius: 12px;
  margin-top: 10px;

  .empty-icon {
    font-size: 36px;
    color: @text-muted;
    margin-bottom: 12px;
  }

  .empty-title {
    font-size: 15px;
    font-weight: 700;
    color: @text-main;
    margin: 0 0 6px 0;
  }

  .empty-desc {
    font-size: 13px;
    color: @text-muted;
    margin: 0 0 20px 0;
  }
}
</style>
